import
  React
from 'react';

import
  styled
from 'styled-components';

import {
  FontFamilies,
  FontSizes,
  Logger,
  MediaQueries,
  DataStore,
  Theme,
  formatPhoneNumber,
  formatPhoneNumberDisplay,
  isValidProviderResult,
} from 'common';

import {
  Button,
  Markdown,
  ShareMeta,
  Text,
} from 'common/components';

import {
  withCommon,
} from 'common/hocs';

import {
  EventDetailsReserveTickets
} from 'ui/event/components';

import {
  ActionContainer,
  Container,
  Content,
  DetailContainer,
  DetailContent,
  DetailContentText,
  DetailsText,
  MainContentContainer,
  MainImage,
  MobileContentSpace,
  SideContentContainer,
  SideContent
} from 'ui/event/styled';

import {
  EventProvider,
} from 'providers';

const MobileHiddenImage = styled.img`
  width: 100%;
  height: auto;
  opacity: 0;

  @media only screen and ${MediaQueries.Md} {
    display: none;
  }
`;

const CalloutOverlay = styled.div`
  position: ${props => props.isMobileFixed ? 'fixed' : 'absolute'};
  top: ${props => props.isMobileFixed ? '85px' : '20px'};
  left: 20px;
  background: ${props => {
    const calloutText = props.children?.toLowerCase();
    if (calloutText === 'tickets available soon' || calloutText?.includes('tickets available in')) {
      return Theme.EventCard.Banner.SuccessColor;
    }
    return Theme.EventCard.Banner.SoldOutColor;
  }};
  color: white;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: ${FontSizes.Smaller};
  font-family: ${FontFamilies.Bold};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  text-transform: ${props => {
    const calloutText = props.children?.toLowerCase();
    if (calloutText === 'tickets available soon') {
      return 'uppercase';
    }
    return 'none';
  }};

  @media only screen and ${MediaQueries.Md} {
    position: absolute;
    top: 20px;
    padding: 10px 12px;
    font-size: ${FontSizes.Small};
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
`;

const EventSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px;
`;

const EventTitle = styled.div`
  color: ${Theme.Text.Color};
  font-family: ${FontFamilies.Bold};
  font-size: 1.35rem;
  line-height: 1.2;

  @media only screen and ${MediaQueries.Md} {
    font-size: 1.65rem;
  }
`;

const EventMeta = styled.div`
  color: ${Theme.Markdown.TextColor};
  font-size: ${FontSizes.Small};
  line-height: 1.5;
`;

const VenueLine = styled.div`
  align-items: baseline;
  color: ${Theme.Text.Color};
  display: flex;
  flex-wrap: wrap;
  font-size: ${FontSizes.Small};
  gap: 8px 12px;
  line-height: 1.5;
`;

const MapLink = styled.a`
  color: ${Theme.Text.Color};
  text-decoration: underline;
`;

const GOOGLE_MAPS_API_KEY = 'AIzaSyAhq4wvpPZY1h-nf08-8tR2G7NI4TTq2wo';

const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
const isWeTicketDomain = hostname === 'weticket.co.za' || hostname.endsWith('.weticket.co.za');
const isWeTicketHost = hostname.includes('localhost') || isWeTicketDomain;

const LocationSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 20px -20px 0;
  padding: 20px 20px 0;
  border-top: 1px solid ${Theme.Divider.Color};
`;

const LocationHeader = styled.div`
  color: ${Theme.Text.Color};
  font-family: ${FontFamilies.Bold};
  font-size: ${FontSizes.Regular};
  line-height: 1.3;
`;

const LocationVenue = styled.div`
  color: ${Theme.Text.Color};
  font-size: ${FontSizes.Small};
  line-height: 1.5;
`;

const LocationMap = styled.iframe`
  border: 0;
  border-radius: 16px;
  display: block;
  height: 280px;
  width: 100%;

  @media only screen and ${MediaQueries.Md} {
    height: 320px;
  }
`;

const getMapPlaceId = (mapLink) => {
  if (!mapLink) {
    return '';
  }

  try {
    const url = new URL(mapLink);
    const queryPlaceId = url.searchParams.get('query_place_id');

    if (queryPlaceId) {
      return queryPlaceId;
    }

    const query = url.searchParams.get('q') || '';

    if (query.startsWith('place_id:')) {
      return query.slice('place_id:'.length);
    }
  } catch (error) {
    // map_link is not a valid URL; try a plain place id match below
  }

  const match = String(mapLink).match(/place_id[:=]([^&]+)/);
  return match ? decodeURIComponent(match[1]) : '';
};

const getMapEmbedUrl = (event) => {
  const placeId = getMapPlaceId(event.map_link);
  const query = placeId
    ? `place_id:${placeId}`
    : (event.address || event.location_display || '');

  if (!query) {
    return '';
  }

  return `https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(query)}&zoom=15`;
};

const OrganiserSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 20px -20px 0;
  padding: 20px 20px 0;
  border-top: 1px solid ${Theme.Divider.Color};
`;

const OrganiserTitle = styled.div``;

const OrganiserLabel = styled.span`
  color: ${Theme.Markdown.TextColor};
  font-size: ${FontSizes.Regular};
  line-height: 1.3;
`;

const OrganiserName = styled.span`
  color: ${Theme.Text.Color};
  font-family: ${FontFamilies.Bold};
  font-size: ${FontSizes.Regular};
  line-height: 1.3;
`;

const OrganiserContacts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OrganiserContact = styled.a`
  align-items: center;
  color: ${Theme.Text.Color};
  display: flex;
  font-size: ${FontSizes.Small};
  gap: 8px;
  overflow-wrap: anywhere;
  text-decoration: none;
  width: fit-content;

  svg {
    flex-shrink: 0;
  }

  span {
    text-decoration: underline;
  }
`;

const MailIcon = ({ color = Theme.Svg.ColorDefault, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" stroke={color} strokeWidth="1.8"/>
    <path d="M4 7l8 6 8-6" stroke={color} strokeWidth="1.8" strokeLinejoin="round"/>
  </svg>
);

const PhoneIcon = ({ color = Theme.Svg.ColorDefault, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M7.2 3.8c.4-.4 1.1-.5 1.6-.2l2.1 1.3c.5.3.7.9.5 1.5l-.7 2.1c-.1.4 0 .8.3 1.1l3.4 3.4c.3.3.7.4 1.1.3l2.1-.7c.6-.2 1.2 0 1.5.5l1.3 2.1c.3.5.2 1.2-.2 1.6l-1.4 1.4c-.6.6-1.5.9-2.4.7-2.2-.4-4.7-2-7.3-4.6S4.9 9.4 4.5 7.2c-.2-.9.1-1.8.7-2.4L7.2 3.8z"
      stroke={color}
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

const useCountdown = (targetDate) => {
  const [timeLeft, setTimeLeft] = React.useState(null);

  React.useEffect(() => {
    // Clear timeLeft when no target date
    if (!targetDate) {
      setTimeLeft(null);
      return;
    }

    const calculateTimeLeft = () => {
      try {
        // Handle different date formats - add 'Z' only if it doesn't already have timezone info
        let target;
        if (targetDate.includes('Z') || targetDate.includes('+') || targetDate.includes('-')) {
          target = new Date(targetDate);
        } else {
          target = new Date(targetDate + 'Z'); // Add 'Z' to ensure UTC parsing
        }
        
        const now = new Date();
        const difference = target.getTime() - now.getTime();

        if (difference > 0) {
          const days = Math.floor(difference / (1000 * 60 * 60 * 24));
          const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((difference % (1000 * 60)) / 1000);

          return { days, hours, minutes, seconds };
        }
        return null;
      } catch (error) {
        console.error('Error calculating countdown:', error);
        return null;
      }
    };

    // Calculate immediately
    const initialTime = calculateTimeLeft();
    setTimeLeft(initialTime);

    // Only set up interval if we have a valid future date
    if (initialTime) {
      const timer = setInterval(() => {
        const newTimeLeft = calculateTimeLeft();
        setTimeLeft(newTimeLeft);
        
        // Clear interval if countdown finished
        if (!newTimeLeft) {
          clearInterval(timer);
        }
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [targetDate]);

  return timeLeft;
};

const formatCountdown = (timeLeft) => {
  if (!timeLeft) return null;
  
  const padZero = (num) => num.toString().padStart(2, '0');
  
  if (timeLeft.days > 0) {
    return `Tickets available in ${timeLeft.days}d ${padZero(timeLeft.hours)}h ${padZero(timeLeft.minutes)}m ${padZero(timeLeft.seconds)}s`;
  } else if (timeLeft.hours > 0) {
    return `Tickets available in ${padZero(timeLeft.hours)}h ${padZero(timeLeft.minutes)}m ${padZero(timeLeft.seconds)}s`;
  } else {
    return `Tickets available in ${padZero(timeLeft.minutes)}m ${padZero(timeLeft.seconds)}s`;
  }
};

const EventDetailsUI = props => {

  const [state, setState] = React.useState({
    event: {}
  });

  const countdownTime = useCountdown(state.event.ticket_sale_start_date);
  const isTicketSaleNotStarted = Boolean(countdownTime);

  const onBuyTicketsClick = () => {
    if (state.event.available_venue_capacity === 0 && state.event.has_seating) {
      props.common.showToast('Tickets sold out');
      return;
    }

    DataStore.set('selectedEvent', state.event);
    props.common.navigate(
      state.event.has_seating
        ? `/event/ticket/identity?instruction=seating`
        : `/event/ticket/select/${state.event.redirect_id || state.event.id}`
    );
  };

  const load = () => {

    if (!props.common?.pathParams?.id) {

      Logger.error('EventDetails', 'load', 'No eventId found');
      props.common.showToast('A technical error occurred');
      return;
    }

    const eId = typeof props.common.pathParams.id === 'string'
      ? props.common.pathParams.id.split('-')[0]
      : props.common.pathParams.id;

    EventProvider.getEventById(eId)
      .then(ret => {

        props.common.hideLoading();

        if (!isValidProviderResult(ret)) {

          if (ret.message === 'Event not live' || ret.message === 'Event not found') {
            Logger.info('EventDetails', 'load', `Error loading event details ${props.common.pathParams.id}: ${ret.message}`);
            props.common.navigate('/');
            return;
          }

          Logger.error('EventDetails', 'load', `Error loading event details ${props.common.pathParams.id}: ${ret.message}`);
          props.common.navigate('/');
          return;
        }

        setState(prevState => ({
          ...prevState,
          event: ret.data
        }));
      })
      .catch(e => {

        // TODO: show error
        Logger.error('EventDetails', 'load', e);
        props.common.showToast('Oh no, we weren\'t able to load your event details, please try again.');

        props.common.hideLoading();
      });
  };

  React.useEffect(load, []); // eslint-disable-line

  const eventDate = state.event.is_multi_event
    ? (state.event.displayDate || 'Multiple dates')
    : (state.event.displayDate ?? '').split(' - ')[0];
  const eventTime = !state.event.is_multi_event && state.event.displayTime
    ? state.event.displayTime
    : null;
  const dateTimeLine = [eventDate, eventTime].filter(Boolean).join('  ·  ');
  const venueName = state.event.location_display || state.event.address;

  // Generate meta description from event data
  const metaDescription = `Get your tickets for ${state.event.name || 'this event'} at ${state.event.location_display || 'the venue'}. Tickets available now.`;
  const pageTitle = state.event.name ? `${state.event.name} - WeTicket` : 'WeTicket - Adventure awaits | Buy Tickets Online';

  const organiser = state.event.organiser || {};
  const organiserName = organiser.name?.trim();
  const organiserEmail = organiser.support_user_email?.trim();
  const organiserPhone = organiser.support_user_phone_number?.trim();
  const hasOrganiserDetails = Boolean(organiserName || organiserEmail || organiserPhone);
  const mapEmbedUrl = isWeTicketHost ? getMapEmbedUrl(state.event) : '';

  return (

    <Container>
      {!props.common.isWidget && (
        <ShareMeta
          title={pageTitle}
          description={metaDescription}
          image={state.event.cover_media_url}>
          {state.event.location_display && <meta name="event:location" content={state.event.location_display} />}
          {state.event.displayDate && <meta name="event:date" content={state.event.displayDate} />}
          {state.event.priceDisplay && <meta name="event:price" content={state.event.priceDisplay} />}
        </ShareMeta>
      )}

      <MainContentContainer boxShadow={Theme.EventDetails.ContentBoxShadow}>
 
        <ImageContainer>
          <MainImage isMobileFixed={!props.common.isWidget} src={state.event.cover_media_url}/>

          {(() => {
            // Show countdown if ticket sale hasn't started yet
            if (state.event.ticket_sale_start_date && countdownTime) {
              return (
                <CalloutOverlay isMobileFixed={!props.common.isWidget} >
                  {formatCountdown(countdownTime)}
                </CalloutOverlay>
              );
            }
            
            // Show regular callout if no countdown or countdown finished
            if (state.event.callout) {
              return (
                <CalloutOverlay isMobileFixed={!props.common.isWidget} >
                  {state.event.callout}
                </CalloutOverlay>
              );
            }
            return null;
          })()}
        </ImageContainer>

        { !props.common.isWidget && <MobileHiddenImage src={state.event.cover_media_url}/> }

        <Content
          backgroundColor={Theme.EventDetails.ContentBackground}
          desktopBackgroundColor={Theme.EventDetails.ContentBackground}>

          <EventSummary>
            <EventTitle>{state.event.name}</EventTitle>
            {dateTimeLine && <EventMeta>{dateTimeLine}</EventMeta>}
            {(venueName || state.event.map_link) && (
              <VenueLine>
                {venueName && <span>{venueName}</span>}
                {state.event.map_link && !isWeTicketHost && (
                  <MapLink href={state.event.map_link} target="_blank" rel="noopener noreferrer">
                    View map
                  </MapLink>
                )}
              </VenueLine>
            )}
          </EventSummary>
          <div style={{ height: '1px', width: '100%', backgroundColor: Theme.Divider.Color }} />
          <DetailsText>

            <div style={{ height: '1rem' }} />

            <Markdown markdown={state.event.description}/>

            {mapEmbedUrl && (
              <LocationSection>
                <LocationHeader>Location</LocationHeader>
                {venueName && <LocationVenue>{venueName}</LocationVenue>}
                <LocationMap
                  title={venueName ? `Map of ${venueName}` : 'Event location'}
                  src={mapEmbedUrl}
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </LocationSection>
            )}

            {hasOrganiserDetails && (
              <OrganiserSection>
                {organiserName && (
                  <OrganiserTitle>
                    <OrganiserLabel>Organised by</OrganiserLabel>&nbsp;
                    <OrganiserName>{organiserName}</OrganiserName>
                  </OrganiserTitle>
                )}
                {(organiserEmail || organiserPhone) && (
                  <OrganiserContacts>
                    {organiserEmail && (
                      <OrganiserContact href={`mailto:${organiserEmail}`}>
                        <MailIcon />
                        <span>{organiserEmail}</span>
                      </OrganiserContact>
                    )}
                    {organiserPhone && (
                      <OrganiserContact href={`tel:${formatPhoneNumber(organiserPhone)}`}>
                        <PhoneIcon />
                        <span>{formatPhoneNumberDisplay(organiserPhone)}</span>
                      </OrganiserContact>
                    )}
                  </OrganiserContacts>
                )}
              </OrganiserSection>
            )}

            <div style={{ height: '30px' }} />

            <MobileContentSpace
              height={'50px'}
            />

          </DetailsText>

        </Content>

        <ActionContainer>
          
          <DetailContent
            padding={'0'}>

            <DetailContentText fontSize={FontSizes.Small}>Price:</DetailContentText>
            <DetailContentText fontSize={'1.2rem'} bold>{state.event.priceDisplay}</DetailContentText>

          </DetailContent>

          <Button
            disabled={isTicketSaleNotStarted}
            onClick={onBuyTicketsClick}>Buy Tickets</Button>

        </ActionContainer>

      </MainContentContainer>

      <SideContentContainer margin={'0 15px 0 20px'}>

        <SideContent top={'0'} sticky={false}>

        {!!state.event.has_seating && (
          <>

            <Text fontSize={FontSizes.Big} fontFamily={FontFamilies.Bold} padding={'20px 20px 15px'}>Reserve Tickets</Text>

            <DetailContainer padding={'20px'}>
              <div style={{ width: '100%' }}>
                <DetailContent
                  padding={'0 0 15px 0'}>

                  <DetailContentText fontSize={FontSizes.Small}>Price:</DetailContentText>
                  <DetailContentText fontSize={'1.2rem'} bold>{state.event.priceDisplay}</DetailContentText>

                </DetailContent>

                <Button
                  disabled={isTicketSaleNotStarted}
                  width={'100%'}
                  onClick={onBuyTicketsClick}>Buy Tickets</Button>
              </div>
            </DetailContainer>
          </>
        )}

        {!state.event.has_seating && (
          <EventDetailsReserveTickets
            tickets={state.event.prices || []}
            event={state.event}
            disabled={isTicketSaleNotStarted}
            onBuyTicketsClick={onBuyTicketsClick}
          />
        )}

        </SideContent>

      </SideContentContainer>

    </Container>
  );
};

export const EventDetails = withCommon(EventDetailsUI, { addDefaultMetaTags: false });